import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type { PaymentState } from "../../../../utils/formatters"

export type OrderPaymentStatus = "Unpaid" | "Paid" | "Refunded"

export enum OrderStatus {
    Active = "active",
    InActive = "inactive",
    Closed = "closed",
    Pending = "pending",
    Declined = "declined",
    Cancelled = "cancelled",
    Ready = "ready",
    Created = "created",
}

export const OrderPaymentTypeEnum = {
    Cash: "cash",
    Card: "card",
    External: "external",
    Redemption: "redemption",
    CardProgram: "card-program",
}
export type OrderPaymentType = (typeof OrderPaymentTypeEnum)[keyof typeof OrderPaymentTypeEnum]

export const OrderSourceEnum = {
    MiB: "mib",
    POS: "pos",
    KIOSK: "kiosk",
    mPOS: "mpos",
}
export type OrderSource = (typeof OrderSourceEnum)[keyof typeof OrderSourceEnum]

export const OrderDeliveryExternalServiceEnum = {
    UBER: "uber",
    GRUBHUB: "grubhub",
    SEAMLESS: "seamless",
    CAVIAR: "caviar",
    DOORDASH: "doordash",
    SLICE: "slice",
    WESTSIDEEATS: "westsideeats",
    FIGURE: "figure",
}

export type OrderDeliveryExternalService =
    (typeof OrderDeliveryExternalServiceEnum)[keyof typeof OrderDeliveryExternalServiceEnum]

export type OrderReportApiParameters = {
    from: string
    to: string
    location_ids: string
    count: number
    // number of orders per page
    offset: number
    timezone: string
    assignee_ids?: string
    search?: string
    source?: OrderSource
    deleted?: boolean
    sort?: string
    sort_order?: string
    // ASC or DESC
    is_active?: boolean
}

export type OrderReportItem = {
    id: string
    orderNumber: string
    opened: string
    closed: Maybe<string>
    deleted: boolean
    assigneeName: Maybe<string>
    assignment: Maybe<string>
    cardNames: string[]
    externalTypes: string[]
    customerName: Maybe<string>
    locationId: string
    locationName: string
    paymentStatus: OrderPaymentStatus
    paymentStatusCode: PaymentState
    paymentTypes: OrderPaymentType[]
    status: OrderStatus
    source: OrderSource
    tipsAmount: Maybe<number>
    gratuityAmount: Maybe<number>
    terminalName: string
    total: Maybe<number>
}

export type OrderItem = {
    id: string
    quantity: number
    name: string
    type: string
    price: number
    orderId: string
    productId: Maybe<string>
    note: Maybe<string>
    originTimestamp: string
    orderedTimestamp: Maybe<string>
    version: number
    basePrice: number
    specialPricingsTotal: number
    subtotalPrice: number
    taxesTotal: number
    totalPrice: number
    orderCourse_id: Maybe<string>
    isActive: boolean
    // For "modification order item", "option order item" to point to the order item it's assigned to
    // Fixed hierarchical pointer to the parent order item indep. of the current category/product hierarchy
    parentOrderItemId: Maybe<string>
    // Link to the category group, to which this "modification or option" belongs
    categoryGroupId: Maybe<string>
    orderItemCategoryGroupId: Maybe<string>
    // TODO: Remove when not useful anymore
    categoryGroupItemId: Maybe<string>
    categoryGroupItemDetailId: Maybe<string>
    // For custom order items (you can set the parent category for printing, reporting, etc...)
    parentCategoryId: Maybe<string>
    supplement: number
    pricings: OrderItemSpecialPricing[]
    categoryGroups: Record<string, any>[]
    unit: Maybe<string>
    unitQuantity: Maybe<number>
}

export type OrderItemSpecialPricing = {
    id: string
    name: string
    percentage: Maybe<number>
    absolute: Maybe<number>
    order_item_id: string
    special_pricing_id: Maybe<string>
    is_active: boolean
    deleted: boolean
    type: string
    origin_timestamp: string
    version: number
    total: number
}

export type OrderItemCategoryGroup = {
    id: string
    category_group_id: string
    product_order_item_id: string
    version: number
    name: string
    type: "modifier" | "option"
    min: Maybe<number>
    max: Maybe<number>
    free: Maybe<number>
    is_exclusion: boolean
    origin_timestamp: string
    display_type: "list" | "scale"
    group_price: Maybe<number>
}

export type FreeCountedOrderItem = {
    orderItem: OrderItem
    free: number
}

export type OrderItemPriceData = {
    orderItemId: string
    free: number
    totalPrice: number
    freeTotal: number
    extraTotal: number
}

export type OrderTransactionPaymentType = {
    type: string
    last4?: string
    external_type?: string
}

export type OrderTransaction = {
    amount?: Maybe<number>
    amountTip?: Maybe<number>
    dateTime: string
    locationName: string
    terminalName: string
    state: string
    number: string
    ccSignature: Maybe<string>
    paymentType: OrderTransactionPaymentType
    type: string
    userName: Maybe<string>
}

export type JoinedItemsPrice = {
    totalPrice: number
    free: number
    freeTotal: number
    extraTotal: number
    item: Maybe<OrderItem>
}

export type JoinedGroupsPrice = {
    groupPrice: Maybe<number>
    group: Maybe<OrderItemCategoryGroup>
    itemPrices: JoinedItemsPrice[]
}

export type GroupItemPriceData = {
    orderItemCategoryGroupId: Maybe<string>
    groupPrice: Maybe<number>
    itemPrices: OrderItemPriceData[]
}

export enum DeliveryService {
    Doordash = "doordash",
    /** @deprecated */
    DoordashDeprecated = "door_dash",
    Relay = "relay",
    Onfleet = "onfleet",
    Ondy = "ondy",
    Shipday = "shipday",
    ATeam = "ateam",
    UberDirect = "uber-direct",
}

export type OrderDetailForClipboard = {
    accountId?: Maybe<string>
    accountName?: Maybe<string>
    orderId?: Maybe<string>
    orderNumber?: Maybe<string>
    orderSource?: Maybe<OrderSource>
    orderStatus?: Maybe<string>
    locationId?: Maybe<string>
    locationName?: Maybe<string>
    terminalId?: Maybe<string>
    terminalName?: Maybe<string>
    created?: Maybe<string>
    createdLocationTime?: Maybe<string>
    createdCzechTime?: Maybe<string>
    gateway?: Maybe<string>
}
